import React, { useEffect, useRef, useState } from 'react';
import IconSelector from '../IconSelector';
import { getMemberExpandKey } from '../../utils/dashboard/memberTreeHelpers';
import { getMemberPresenceStatus } from '../../utils/dashboard/memberMarkers';
import { getMemberListDisplayName } from '../../utils/dashboard/commanderMemberNoteHelpers';
import { PRESENCE_DOT_COLORS, PRESENCE_STATUS } from '../../utils/dashboard/constants';

function formatMemberSeen(lastSeen) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  const pad = (n) => String(n).padStart(2, '0');
  if (sameDay) {
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} 更新`;
  }
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())} 更新`;
}

function formatFullSeen(lastSeen) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `更新于 ${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const actionBtn = {
  height: 28,
  padding: '0 10px',
  fontSize: 12,
  borderRadius: 6,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  border: '1px solid #d0d7de',
  background: '#fff',
  color: '#333'
};

const actionBtnPrimary = {
  ...actionBtn,
  background: '#1976d2',
  borderColor: '#1976d2',
  color: '#fff',
  fontWeight: 500
};

const dropMenu = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  marginBottom: 4,
  minWidth: 132,
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
  padding: 4,
  zIndex: 30
};

const dropItem = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '7px 10px',
  fontSize: 12,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 4,
  color: '#333'
};

function MemberDetail({
  member,
  onFlyTo,
  onToggleTrack,
  onStartRealtimeTrack,
  onStopRealtimeTrack,
  isRealtimeDrawing = false,
  hasDeviceTrack = false,
  onCopyCoords,
  onDownloadTrack,
  onFetchTrack,
  onIconChange,
  onOpenLiveStream,
  isStreaming = false,
  isHiddenOnMap = false,
  onToggleMapHidden,
  commandFeatures = {}
}) {
  const [trackOpen, setTrackOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [iconEdit, setIconEdit] = useState(false);
  const boxRef = useRef(null);
  const deviceId = member.device_unique_id || member.name;

  useEffect(() => {
    if (!trackOpen && !moreOpen) return undefined;
    const onDown = (e) => {
      if (boxRef.current?.contains?.(e.target)) return;
      setTrackOpen(false);
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [trackOpen, moreOpen]);

  const fmtNum = (v, unit = '', digits = null) => {
    if (v == null || v === '') return '未知';
    const n = Number(v);
    if (!Number.isFinite(n)) return '未知';
    const text = digits != null ? n.toFixed(digits) : String(n);
    return `${text}${unit}`;
  };
  const canFetch = commandFeatures.fetchTrack !== false;
  const canView = commandFeatures.viewTrack !== false;
  const canDownload = commandFeatures.downloadTrack !== false;
  const canRealtime = commandFeatures.realtimeDraw !== false;

  const cell = (label, value) => (
    <div style={{ minWidth: 0 }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ marginLeft: 4, color: '#222', fontWeight: 500 }}>{value}</span>
    </div>
  );

  return (
    <div
      ref={boxRef}
      style={{
        padding: '10px 12px',
        background: '#f7fafc',
        fontSize: 12,
        borderTop: '1px solid #e6edf2'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {formatFullSeen(member.last_seen) && (
        <div style={{ marginBottom: 8, color: '#666', fontSize: 11 }}>
          {formatFullSeen(member.last_seen)}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '6px 12px',
        marginBottom: 10
      }}>
        {cell('经度', member.last_longitude != null ? Number(member.last_longitude).toFixed(6) : '未知')}
        {cell('纬度', member.last_latitude != null ? Number(member.last_latitude).toFixed(6) : '未知')}
        {cell('海拔', member.last_altitude != null ? `${member.last_altitude} m` : '未知')}
        {cell('精度', fmtNum(member.last_accuracy, ' m'))}
        {cell('速度', fmtNum(member.last_speed, ' m/s', 1))}
        {cell('方向', fmtNum(member.last_bearing, '°', 1))}
        {cell('电量', member.battery_level != null && member.battery_level !== '' ? `${member.battery_level}%` : '未知')}
        {cell('信号', member.gsm != null && member.gsm !== '' ? member.gsm : '未知')}
      </div>

      <div style={{
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap'
      }}>
        <b style={{ color: '#555' }}>图标</b>
        {iconEdit ? (
          <IconSelector
            value={member.icon || undefined}
            placeholder="默认图标"
            size="small"
            showPreview
            deviceId={deviceId}
            style={{ minWidth: 160, fontSize: 11 }}
            onChange={(icon) => {
              onIconChange?.(member, icon);
              setIconEdit(false);
            }}
            onIconChange={(icon) => {
              onIconChange?.(member, icon);
              setIconEdit(false);
            }}
          />
        ) : (
          <>
            <span style={{ color: '#333' }}>
              {member.icon || '默认图标'}
            </span>
            <button
              type="button"
              onClick={() => setIconEdit(true)}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#1976d2',
                cursor: 'pointer',
                fontSize: 12,
                padding: 0
              }}
            >
              编辑
            </button>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          style={actionBtnPrimary}
          onClick={(e) => { e.stopPropagation(); onFlyTo?.(member); }}
          title="定位到该队员"
        >
          📍 定位
        </button>
        <button
          type="button"
          style={actionBtn}
          onClick={(e) => {
            e.stopPropagation();
            if (member.last_longitude != null && member.last_latitude != null) {
              onCopyCoords?.(`${Number(member.last_longitude).toFixed(6)},${Number(member.last_latitude).toFixed(6)}`);
            } else {
              alert('该队员暂无有效坐标');
            }
          }}
          title="复制坐标"
        >
          复制坐标
        </button>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            style={trackOpen ? actionBtnPrimary : actionBtn}
            onClick={(e) => {
              e.stopPropagation();
              setMoreOpen(false);
              setTrackOpen((v) => !v);
            }}
            title="轨迹操作"
          >
            轨迹 ▾
          </button>
          {trackOpen && (
            <div style={dropMenu} role="menu">
              {canFetch && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    onFetchTrack?.(member);
                  }}
                  title="抓取队员设备已记录并上传的轨迹数据"
                >
                  获取轨迹
                </button>
              )}
              {canRealtime && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    if (isRealtimeDrawing) onStopRealtimeTrack?.(member);
                    else onStartRealtimeTrack?.(member);
                  }}
                  title={isRealtimeDrawing
                    ? '停止续画：已绘轨迹会保留在地图上'
                    : '按队员实时坐标变化在地图上连线绘制轨迹'}
                >
                  {isRealtimeDrawing ? '停止绘制' : '绘制轨迹'}
                </button>
              )}
              {canView && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    onToggleTrack?.(member);
                  }}
                  title="显示或隐藏该队员已在地图上的轨迹（历史/绘制）"
                >
                  {hasDeviceTrack ? '隐藏轨迹' : '查看轨迹'}
                </button>
              )}
              {canDownload && (
                <button
                  type="button"
                  style={dropItem}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackOpen(false);
                    onDownloadTrack?.(member);
                  }}
                >
                  下载轨迹
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            style={{ ...actionBtn, padding: '0 8px', minWidth: 32, justifyContent: 'center' }}
            onClick={(e) => {
              e.stopPropagation();
              setTrackOpen(false);
              setMoreOpen((v) => !v);
            }}
            title="更多操作"
          >
            ···
          </button>
          {moreOpen && (
            <div style={{ ...dropMenu, left: 'auto', right: 0 }} role="menu">
              <button
                type="button"
                style={dropItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreOpen(false);
                  if (isStreaming) onOpenLiveStream?.(member);
                  else alert(`${member.name} 当前未在直播`);
                }}
              >
                {isStreaming ? '观看直播' : '直播（未开播）'}
              </button>
              <button
                type="button"
                style={dropItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setMoreOpen(false);
                  onToggleMapHidden?.(member);
                }}
              >
                {isHiddenOnMap ? '地图上显示' : '地图上隐藏'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 队员树单行（含展开详情）
 * 默认只看状态；点开查看数据；低频操作收入「轨迹 / 更多」
 */
export default function MemberTreeRow({
  member,
  theme,
  openMember,
  highlightedMemberName,
  showTracks = {},
  isMemberTrackVisible,
  presenceById = null,
  onlineTimeoutMs,
  offlineTimeoutMs,
  nameAnchorRefs,
  isStreaming,
  isHiddenOnMap,
  isDrawing,
  toggleMemberExpand,
  openCommandMenuFor,
  flyToMember,
  setActiveMemberId,
  setHighlightedMemberName,
  toggleTrack,
  openLiveStream,
  onToggleMemberMapHidden,
  startRealTimeTrackDrawing,
  stopTrackFeatures,
  copyToClipboard,
  openDownloadTrackModal,
  fetchMemberTrack,
  onMemberIconChange,
  commandFeatures = {},
  notesByKey = null
}) {
  const memberId = member.device_unique_id || member.name;
  const displayName = getMemberListDisplayName(member, notesByKey);
  const presence = (presenceById && memberId && presenceById[memberId])
    || getMemberPresenceStatus(
      member,
      Date.now(),
      onlineTimeoutMs,
      offlineTimeoutMs
    );
  const presenceLabel = presence === PRESENCE_STATUS.ONLINE
    ? '在线'
    : (presence === PRESENCE_STATUS.TEMP_OFFLINE ? '暂时离线' : '离线');
  const memberKey = getMemberExpandKey(member);
  const isExpanded = !!openMember[memberKey];
  const batteryText = member.battery_level != null ? `电量 ${member.battery_level}%` : null;
  const seenText = formatMemberSeen(member.last_seen);
  const nameTooltip = [
    displayName,
    presenceLabel,
    batteryText,
    seenText,
    member.org_name ? `组织 ${member.org_name}` : null,
    member.group_name ? `小组 ${member.group_name}` : null,
    member.device_unique_id ? `设备 ${member.device_unique_id}` : null
  ].filter(Boolean).join('\n');
  const t = theme;
  const streaming = isStreaming(member.name);
  const hidden = isHiddenOnMap(member);
  const trackVisible = isMemberTrackVisible
    ? isMemberTrackVisible(member.name)
    : !!showTracks[member.name];
  const isHighlighted = highlightedMemberName === member.name;

  const rowBg = isExpanded
    ? '#e8f1fb'
    : (isHighlighted ? '#fff4e5' : '#fff');

  return (
    <div key={member.device_unique_id || member.name} style={t.memberWrap}>
      <div
        style={{
          ...t.memberRow(isExpanded),
          background: rowBg,
          borderColor: isExpanded ? '#b5d0ea' : '#e9ecef'
        }}
        onClick={(e) => {
          // 点击行空白处展开/收起（名字、按钮自行 stop）
          if (e.target !== e.currentTarget && e.target.closest?.('button, a, input, select')) return;
        }}
      >
        <span
          style={{
            fontSize: 12,
            cursor: 'pointer',
            color: '#1976d2',
            marginRight: 2,
            userSelect: 'none',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-flex',
            width: 14,
            justifyContent: 'center',
            flexShrink: 0
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggleMemberExpand(member, memberKey, isExpanded);
          }}
          title={isExpanded ? '收起' : '展开详情'}
        >
          ▶
        </span>
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: PRESENCE_DOT_COLORS[presence] || PRESENCE_DOT_COLORS.offline,
            flexShrink: 0
          }}
          title={presenceLabel}
        />
        <span
          ref={(el) => {
            const id = member.device_unique_id || member.name;
            if (el) nameAnchorRefs.current[id] = el;
            else delete nameAnchorRefs.current[id];
          }}
          style={{
            fontWeight: 600,
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '46%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isHighlighted ? '#d32f2f' : '#222',
            cursor: 'pointer'
          }}
          title={nameTooltip}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const id = member.device_unique_id || member.name;
            flyToMember?.(member);
            setActiveMemberId?.(id);
            setHighlightedMemberName?.(member.name);
            openCommandMenuFor(member, e.currentTarget);
          }}
        >
          {displayName}
        </span>
        {/* 状态/更新/电量右对齐，贴合最右直播钮 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            flex: '0 1 auto',
            minWidth: 0,
            marginLeft: 'auto',
            overflow: 'hidden',
            fontSize: 11,
            color: '#666',
            whiteSpace: 'nowrap'
          }}
          title={[presenceLabel, seenText, batteryText].filter(Boolean).join(' | ')}
        >
          <span
            style={{
              flexShrink: 0,
              color: presence === PRESENCE_STATUS.ONLINE ? '#2e7d32' : '#8a93a0',
              fontWeight: 500
            }}
          >
            {presenceLabel}
          </span>
          {seenText ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{seenText}</span>
          ) : null}
          {batteryText ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, color: '#555' }}>
              {batteryText}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!streaming) return;
            openLiveStream?.(member);
          }}
          title={streaming ? '观看前线直播' : '直播未在线'}
          aria-label={streaming ? '观看直播' : '直播未在线'}
          style={{
            flexShrink: 0,
            marginLeft: 6,
            height: 22,
            minWidth: 40,
            padding: '0 8px',
            borderRadius: 4,
            border: streaming ? 'none' : '1px solid #d0d5dd',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            cursor: streaming ? 'pointer' : 'default',
            background: streaming ? '#e53935' : '#eceff1',
            color: streaming ? '#fff' : '#90a4ae',
            lineHeight: '22px'
          }}
        >
          直播
        </button>
      </div>
      {isExpanded && (
        <MemberDetail
          member={member}
          onFlyTo={flyToMember}
          onToggleTrack={() => toggleTrack?.(member.name)}
          onStartRealtimeTrack={() => startRealTimeTrackDrawing?.(member.name)}
          onStopRealtimeTrack={() => stopTrackFeatures?.(member.name)}
          isRealtimeDrawing={isDrawing(member.name)}
          hasDeviceTrack={trackVisible}
          onCopyCoords={copyToClipboard}
          onDownloadTrack={openDownloadTrackModal}
          onFetchTrack={(m) => fetchMemberTrack?.(m)}
          onIconChange={onMemberIconChange}
          onOpenLiveStream={openLiveStream}
          isStreaming={streaming}
          isHiddenOnMap={hidden}
          onToggleMapHidden={onToggleMemberMapHidden}
          commandFeatures={commandFeatures}
        />
      )}
    </div>
  );
}
